import { clientEnvModule } from "@/modules/client-env";
import { RecallPermissionKind, RecallPermissionStatus } from "@/modules/electron/types";

/**
 * Should match the definition in our `mem-client-desktop` project.
 */
type ElectronAPI = {
  navigateToInternalUrl: (args: { url: string }) => Promise<void>;
  navigateToExternalUrl: (args: { url: string }) => Promise<void>;
  findInPage: (args: { text: string; options?: unknown }) => Promise<void>;
  stopFindInPage: (args: { action: "clearSelection" | "keepSelection" }) => Promise<void>;
  onFindInPageResult: (callback: (event: unknown, result: unknown) => void) => void;
  toggleMenu: () => Promise<void>;
  minimizeWindow: () => Promise<void>;
  maximizeWindow: () => Promise<void>;
  closeWindow: () => Promise<void>;
  // Passing tells the handler to keep checking for updates, even if there isn't one available yet.
  updateNow: (waitForUpdate: boolean) => Promise<boolean>;
  restartApp?: () => Promise<void>;
  openSystemSoundSettings?: (args?: { focus?: "input" | "output" }) => Promise<void>;
  /**
   * Events related to the Recall.ai Desktop SDK
   */
  recall: {
    prepareDesktopRecording: () => Promise<{ windowId: string }>;
    startRecording: (args: { windowId: string; uploadToken: string }) => Promise<void>;
    pause: (args: { windowId: string }) => Promise<void>;
    resume: (args: { windowId: string }) => Promise<void>;
    stop: (args: { windowId: string }) => Promise<void>;
    onEvent?: (cb: (payload: { event: unknown }) => void) => () => void;
    requestPermission: (permission: RecallPermissionKind) => Promise<void>;
    getPermissionStatus: () => Promise<Record<RecallPermissionKind, RecallPermissionStatus>>;
    onPermissionStatus?: (
      cb: (payload: {
        event: { permission: RecallPermissionKind; status: RecallPermissionStatus };
      }) => void
    ) => () => void;
    openPermissionPane: (permission: RecallPermissionKind) => Promise<void>;
  };
};

export const getElectronAPI = (): ElectronAPI | null => {
  const isDesktop = clientEnvModule.isDesktop();

  if (isDesktop) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any).electronAPI as ElectronAPI;
  }

  return null;
};
